import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import {
  type ApiError,
  type ApiResponse,
  addActionNameToMutationResult,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { z } from 'zod/v4';
import {
  FixFeeSchema,
  InterestSchema,
  PenaltyClauseSchema,
  TermsAndConditionsSchema,
} from './use-load-terms-and-conditions-settings';

const URL = '/terms_and_conditions_settings';

// The document lives on its own endpoint, and `deletable` is derived server
// side, so neither travels back on a write. A set without an id is a new one.
//
// This is also where the ranges the read schema stays quiet about are enforced:
// legacy rows may hold values the model never validated, and the user has to fix
// them before they can be saved back.
export const TermsAndConditionsVariablesSchema = TermsAndConditionsSchema.omit({
  id: true,
  hasPDF: true,
  pdfFilename: true,
  pdfUrl: true,
  deletable: true,
}).extend({
  id: z.number().optional(),
  name: z.string().min(1),
  minimumPaymentTermsDelay: z.number().int().min(0),
  interest: InterestSchema.extend({ value: z.number().min(0).max(100) }).nullable(),
  fixFee: FixFeeSchema.extend({ value: z.number().min(0) }).nullable(),
  penaltyClause: PenaltyClauseSchema.extend({
    value: z.number().min(0),
    minimum: z.number().min(0),
  }).nullable(),
});

// The list is declarative: whatever it contains becomes the full set of terms,
// and anything left out is deleted.
const UpdateTermsAndConditionsSettingsVariablesSchema = z.object({
  termsAndConditions: z.array(TermsAndConditionsVariablesSchema).optional(),
  sendTOS: z.boolean().optional(),
  ignoreLateFees: z.boolean().optional(),
});

export type UpdateTermsAndConditionsSettingsVariables = z.infer<
  typeof UpdateTermsAndConditionsSettingsVariablesSchema
>;

export async function updateTermsAndConditionsSettingsFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: UpdateTermsAndConditionsSettingsVariables,
) {
  const instance = await axiosInstance;

  return instance.put(URL, UpdateTermsAndConditionsSettingsVariablesSchema.parse(variables));
}

export function useUpdateTermsAndConditionsSettings() {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<ApiResponse, ApiError, UpdateTermsAndConditionsSettingsVariables>({
    mutationFn: variables => updateTermsAndConditionsSettingsFn(axiosInstance, variables),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['termsAndConditionsSettings'] });
      client.invalidateQueries({ queryKey: ['companyConfigurationV2'] });
    },
  });

  return addActionNameToMutationResult<
    ApiResponse,
    ApiError,
    UpdateTermsAndConditionsSettingsVariables,
    'updateTermsAndConditionsSettings'
  >('updateTermsAndConditionsSettings', mutation);
}
